import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Users, Clock, ChevronRight, CheckCircle2,
  Briefcase, GraduationCap, Building, DollarSign, Zap, FileText,
  MessageCircle, Pencil, X, Plus, Filter
} from 'lucide-react';
import confetti from 'canvas-confetti';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  career_day: Briefcase, college_visit: GraduationCap, college_fair: Building,
  employer_panel: Users, scholarship_workshop: DollarSign, wbl_fair: Zap,
  fafsa_workshop: FileText, counselor_meeting: MessageCircle, test_prep: Pencil, other: Calendar,
};

const COLOR_CLASSES = {
  cyan: { card: 'border-cyan-500/20 bg-cyan-500/5', badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30', btn: 'bg-cyan-600 hover:bg-cyan-500' },
  blue: { card: 'border-blue-500/20 bg-blue-500/5', badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30', btn: 'bg-blue-600 hover:bg-blue-500' },
  indigo: { card: 'border-indigo-500/20 bg-indigo-500/5', badge: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30', btn: 'bg-indigo-600 hover:bg-indigo-500' },
  violet: { card: 'border-violet-500/20 bg-violet-500/5', badge: 'bg-violet-500/15 text-violet-300 border-violet-500/30', btn: 'bg-violet-600 hover:bg-violet-500' },
  amber: { card: 'border-amber-500/20 bg-amber-500/5', badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30', btn: 'bg-amber-600 hover:bg-amber-500' },
  emerald: { card: 'border-emerald-500/20 bg-emerald-500/5', badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', btn: 'bg-emerald-600 hover:bg-emerald-500' },
  green: { card: 'border-green-500/20 bg-green-500/5', badge: 'bg-green-500/15 text-green-300 border-green-500/30', btn: 'bg-green-600 hover:bg-green-500' },
  rose: { card: 'border-rose-500/20 bg-rose-500/5', badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30', btn: 'bg-rose-600 hover:bg-rose-500' },
  orange: { card: 'border-orange-500/20 bg-orange-500/5', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30', btn: 'bg-orange-600 hover:bg-orange-500' },
  slate: { card: 'border-slate-600/30 bg-slate-700/20', badge: 'bg-slate-700 text-slate-300 border-slate-600', btn: 'bg-slate-600 hover:bg-slate-500' },
};

function EventCard({ event, onRsvp }) {
  const [rsvping, setRsvping] = useState(false);
  const Icon = ICONS[event.type] || Calendar;
  const colors = COLOR_CLASSES[event.type_color] || COLOR_CLASSES.slate;
  const spotsFull = event.rsvp_count >= (event.capacity || 9999);
  const spotsLeft = event.capacity ? event.capacity - event.rsvp_count : null;

  const handleRsvp = async () => {
    setRsvping(true);
    await onRsvp(event.id, !event.user_rsvped);
    setRsvping(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border p-5 hover:border-opacity-50 transition-all ${colors.card}`}
      data-testid="event-card"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 ${colors.badge}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${colors.badge} mb-1 inline-block`}>
                {event.type_label}
              </span>
              <h3 className="text-white font-semibold leading-snug">{event.title}</h3>
            </div>
            {event.user_rsvped && (
              <span className="flex items-center gap-1 text-xs text-emerald-400 flex-shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" /> RSVP'd
              </span>
            )}
          </div>

          <p className="text-slate-300 text-xs mt-1.5 line-clamp-2">{event.description}</p>

          <div className="flex items-center gap-4 mt-3 text-xs text-slate-300 flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {event.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {event.time}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              <span className={event.is_virtual ? 'text-cyan-400' : ''}>
                {event.is_virtual ? 'Virtual' : event.location}
              </span>
            </span>
            {event.capacity && (
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                {event.rsvp_count}/{event.capacity}
                {spotsLeft && spotsLeft <= 10 && <span className="text-orange-400 ml-1">- {spotsLeft} spots left!</span>}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {event.tags?.slice(0, 3).map(tag => (
              <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/50">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        {event.user_rsvped ? (
          <button onClick={handleRsvp} disabled={rsvping}
            className="text-xs text-slate-300 hover:text-red-400 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-500/10">
            {rsvping ? '...' : 'Cancel RSVP'}
          </button>
        ) : (
          <button onClick={handleRsvp} disabled={rsvping || (spotsFull && !event.user_rsvped)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors disabled:opacity-50 ${colors.btn}`}
            data-testid={`rsvp-${event.id}`}>
            {rsvping ? 'RSVP\'ing...' : spotsFull ? 'Full' : <><Plus className="w-3 h-3" /> RSVP</>}
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function EventSchedulerPage() {
  const { getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [myRsvps, setMyRsvps] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/events${filterType ? `?type=${filterType}` : ''}`, getAuthHeader());
      setEvents(r.data.events || []);
      if (!eventTypes.length) setEventTypes(r.data.event_types || []);
    } catch { toast.error('Could not load events'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterType]);

  const handleRsvp = async (eventId, rsvping) => {
    try {
      if (rsvping) {
        await axios.post(`${API}/events/${eventId}/rsvp`, {}, getAuthHeader());
        confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 }, colors: ['#06b6d4', '#10b981'] });
        toast.success('You\'re in! See you there.');
      } else {
        await axios.delete(`${API}/events/${eventId}/rsvp`, getAuthHeader());
        toast.success('RSVP cancelled');
      }
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, user_rsvped: rsvping, rsvp_count: e.rsvp_count + (rsvping ? 1 : -1) }
        : e));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('Already RSVP\'d');
      } else {
        toast.error('Could not update RSVP');
      }
    }
  };

  const displayed = myRsvps ? events.filter(e => e.user_rsvped) : events;
  const myCount = events.filter(e => e.user_rsvped).length;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-12" data-testid="event-scheduler-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Events & Opportunities</span>
              </div>
              <h1 className="text-4xl font-bold text-white">Event Scheduler</h1>
              <p className="text-slate-300 text-sm mt-1">Career days, college visits, scholarship workshops, and more.</p>
            </div>
            {myCount > 0 && (
              <button onClick={() => setMyRsvps(!myRsvps)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors ${myRsvps ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'border-slate-700 text-slate-300 hover:text-white hover:border-slate-600'}`}>
                <CheckCircle2 className="w-4 h-4" />
                My Events ({myCount})
              </button>
            )}
          </div>
        </motion.div>

        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilterType('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${!filterType ? 'bg-white/10 border-white/20 text-white' : 'border-slate-700 text-slate-300 hover:text-white'}`}>
            All Events
          </button>
          {eventTypes.map(t => (
            <button key={t.id} onClick={() => setFilterType(filterType === t.id ? '' : t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${filterType === t.id ? 'bg-white/10 border-white/20 text-white' : 'border-slate-700 text-slate-300 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Events */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-12 text-slate-300">
            <Calendar className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>{myRsvps ? "You haven't RSVP'd to any events yet." : "No events found."}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayed.map((event, i) => (
              <EventCard key={event.id} event={event} onRsvp={handleRsvp} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
